import axios from "axios";
import {
  FromWorkerMessage,
  MessageType,
  OperationType,
  ErrorType,
  TaskType,
} from "./entities";
import { createLogger } from "@/debug/DebugingLogger";
const log = createLogger("Worker:Utils");

function sendMessage(message: FromWorkerMessage): void {
  self.postMessage(message);
}

let sessionExpiredNotified = false;

interface AxiosLikeResponse {
  status: number;
  headers?: Record<string, string>;
}

function checkSessionExpired(response: Response | AxiosLikeResponse): boolean {
  let status: number;
  let sessionExpiredHeader: string | null;

  // Handle Fetch API Response
  if (response instanceof Response) {
    status = response.status;
    sessionExpiredHeader = response.headers.get("x-session-expired");
  } else {
    // Handle Axios response
    status = response.status;
    sessionExpiredHeader = response.headers?.["x-session-expired"] || null;
  }

  if (status === 401 && sessionExpiredHeader === "true") {
    if (!sessionExpiredNotified) {
      sessionExpiredNotified = true;
      sendMessage({ task: TaskType.SessionExpired });
    }
    return true;
  }
  return false;
}

/**
 * Wraps fetch() with session expiration detection.
 */
async function fetchWithSessionRetry(
  input: RequestInfo | URL,
  init?: RequestInit & Record<string, unknown>,
): Promise<Response> {
  const response = await fetch(input, init);
  checkSessionExpired(response);
  return response;
}

// ---------------------------------------------------------------------------
// Quota check — shared by import and upload paths
// ---------------------------------------------------------------------------

function canUploadFile(
  totalQuota: number,
  usedQuota: number,
  fileSize: number,
): boolean {
  return totalQuota - usedQuota >= fileSize;
}

/// Some targets are billed to nobody — a catalog model's storage is
/// role-owned and unmetered (D20) — so there is no headroom to compare
/// against and the upload is always within budget.
function isUnmetered(quota: { unmetered?: boolean }): boolean {
  return quota.unmetered === true;
}

const handleCheckUserQuota = async (modelId?: string | null) => {
  try {
    const response = await axios.get("check_user_quota", {
      params: modelId ? { model_id: modelId } : {},
      withCredentials: true,
      baseURL: import.meta.env.VITE_APP_STORAGE_SERVICE,
    });
    return response;
  } catch (error: unknown) {
    if (error instanceof Error && "response" in error) {
      const axiosError = error as { response?: AxiosLikeResponse };
      if (axiosError.response && checkSessionExpired(axiosError.response)) {
        throw new Error("SESSION_EXPIRED");
      }
    }
    log.error("Quota check failed:", error);
    throw error;
  }
};

function sendUploadError(
  filename: string,
  errorType: ErrorType,
  message: string,
) {
  sendMessage({
    type: MessageType.Error,
    errorType,
    error: message,
    message,
    filename,
    operation: OperationType.Upload,
  });
}

/**
 * Check that the user has enough quota to upload `fileBytes` more.
 * On insufficient quota, emits an upload error and returns false.
 * Callers should bail out (return) when this returns false.
 */
async function checkUploadQuota(
  modelID: string,
  fileBytes: number,
  filename: string,
): Promise<boolean> {
  const quota = await handleCheckUserQuota(modelID);
  if (isUnmetered(quota.data)) return true;
  if (
    !canUploadFile(
      quota.data.total_quota_bytes,
      quota.data.used_quota_bytes,
      fileBytes,
    )
  ) {
    sendUploadError(
      filename,
      ErrorType.Quota,
      "Upload failed, insufficient quota!",
    );
    return false;
  }
  return true;
}

/**
 * Send a progress message for an upload or download operation.
 */
function sendProgress(
  filename: string,
  progress: number,
  operation: OperationType,
): void {
  sendMessage({ type: MessageType.Progress, progress, filename, operation });
}

/**
 * Convert a thrown value into a user-visible error message and ship it via
 * postMessage. Used by all worker task handlers that need to surface a
 * `MessageType.Error` to the main thread.
 */
function sendOperationError(
  e: unknown,
  errorType: ErrorType,
  operation: OperationType,
  filename: string,
  prefix: string,
): void {
  const msg = e instanceof Error ? e.message : String(e);
  sendMessage({
    type: MessageType.Error,
    errorType,
    error: msg,
    message: `${prefix}: ${msg}`,
    filename,
    operation,
  });
}

// ---------------------------------------------------------------------------
// Client-error telemetry — fire-and-forget POST to /server/api/public/client_errors
// ---------------------------------------------------------------------------

export type ClientErrorKind =
  | "streaming_failed"
  | "buffered_fallback_succeeded"
  | "buffered_fallback_failed"
  | "buffered_cap_exceeded";

export interface ClientErrorReport {
  kind: ClientErrorKind;
  name: string;
  message: string;
  file_size: number | null;
  model_id: string | null;
}

interface ClientErrorPayload extends ClientErrorReport {
  ua: string;
}

/**
 * Fire-and-forget telemetry to the server. Used by the upload path to record
 * streaming failures and buffered-fallback outcomes so we can quantify the
 * corporate-firewall failure rate without parsing support tickets.
 *
 * Best-effort: no `await`, no retry, errors swallowed. `keepalive: true` lets
 * the request survive worker / page teardown.
 */
function reportClientError(report: ClientErrorReport): void {
  try {
    const baseUrl = import.meta.env.VITE_APP_BASE_URL as string | undefined;
    if (!baseUrl) return;
    const url = new URL("api/public/client_errors", `${baseUrl}/`);
    const payload: ClientErrorPayload = {
      ...report,
      ua: self.navigator?.userAgent ?? "",
    };
    void fetch(url.toString(), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "include",
    }).catch(() => {
      /* swallow — best-effort telemetry */
    });
  } catch {
    /* swallow — never let telemetry break the upload path */
  }
}

export {
  sendMessage,
  checkSessionExpired,
  fetchWithSessionRetry,
  canUploadFile,
  handleCheckUserQuota,
  sendUploadError,
  reportClientError,
  checkUploadQuota,
  sendProgress,
  sendOperationError,
};
